import React from "react";
import PropTypes from 'prop-types';

const LaundryInvoice = ({ data }) => {
  if (!data) return null;

  const {
    invoice_no,
    send_date,
    received_date,
    payment_status,
    total_amount,
    remarks,
    operated_by,
    items = [],
  } = data;

  return (
    <div className="print-invoice card p-4">
      {/* ===== PRINT-ONLY STYLES (LOCAL) ===== */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }

            .print-invoice,
            .print-invoice * {
              visibility: visible;
            }

            .print-invoice {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: #fff;
              padding: 20px;
            }

            .card {
              box-shadow: none !important;
              border: none !important;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th, td {
              border: 1px solid #000;
              padding: 6px;
            }

            .btn {
              display: none !important;
            }
          }
        `}
      </style>

      {/* ================= HEADER ================= */}
      <div className="d-flex justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1">Laundry Invoice</h4>
          <div className="text-muted">Invoice No: {invoice_no}</div>
        </div>

        <div>
          <span
            className={`badge ${payment_status === "paid" ? "bg-success" : "bg-warning"
              }`}
          >
            {payment_status}
          </span>
        </div>
      </div>

      {/* ================= META INFO ================= */}
      <div className="row mb-4">
        <div className="col-md-4">
          <strong>Operated By</strong>
          <div>{operated_by?.employee_name}</div>
        </div>

        <div className="col-md-4">
          <strong>Send Date</strong>
          <div>{send_date}</div>
        </div>

        <div className="col-md-4">
          <strong>Received Date</strong>
          <div>{received_date}</div>
        </div>
      </div>

      {/* ================= ITEMS TABLE ================= */}
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Qty</th>
              <th>Tasks</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const taskTotal = item.tasks.reduce(
                (sum, task) => sum + Number(task.price),
                0
              );

              const itemTotal = taskTotal * item.quantity;

              return (
                <tr key={item.laundry_item_id}>
                  <td>{index + 1}</td>
                  <td>{item.product_name}</td>
                  <td>{item.quantity}</td>
                  <td>
                    {item.tasks.map((task) => (
                      <div key={task.task_id}>
                        {task.task_name} – ₹{task.price}
                      </div>
                    ))}
                  </td>
                  <td>₹{itemTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="row mt-4">
        <div className="col-md-8">
          <strong>Remarks</strong>
          <div>{remarks || "—"}</div>
        </div>

        <div className="col-md-4 text-end">
          <h5 className="fw-bold">
            Total: ₹{Number(total_amount).toFixed(2)}
          </h5>
        </div>
      </div>
    </div>
  );
};

LaundryInvoice.propTypes = {
  data: PropTypes.shape({
    invoice_no: PropTypes.string,
    send_date: PropTypes.string,
    received_date: PropTypes.string,
    payment_status: PropTypes.string,
    total_amount: PropTypes.number,
    remarks: PropTypes.string,
    operated_by: PropTypes.shape({
      employee_name: PropTypes.string,
    }),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        laundry_item_id: PropTypes.number,
        product_name: PropTypes.string,
        quantity: PropTypes.number,
        tasks: PropTypes.arrayOf(
          PropTypes.shape({
            task_id: PropTypes.number,
            task_name: PropTypes.string,
            price: PropTypes.number,
          })
        ),
      })
    ),
  }).isRequired,
};

export default LaundryInvoice;
